import { store } from "../app/state/store.js";
import { labelFor } from "../theme/naming.js";
import { iconFor } from "../theme/icons.js";
import { verdictLabel } from "../game/scoring.js";
import { escapeHtml } from "../game/util/escape.js";
const VERDICT_COLOR = {
    perfect: "#4ade80",
    early: "#fbbf24",
    late: "#fb923c",
    free: "#22d3ee",
    miss: "#f87171",
    none: "#8891b5",
};
export class AcHud extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "unsub", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "tick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
    }
    connectedCallback() {
        this.style.position = "absolute";
        this.style.inset = "0";
        this.style.pointerEvents = "none";
        this.style.fontVariantNumeric = "tabular-nums";
        this.unsub = store.hud.subscribe((h) => this.render(h));
        this.tick = window.setInterval(() => this.render(store.hud.get()), 300);
    }
    disconnectedCallback() {
        this.unsub?.();
        if (this.tick)
            clearInterval(this.tick);
    }
    render(h) {
        const theme = "child";
        const stat = (label, val) => `<div style="background:rgba(18,22,42,.72);padding:8px 12px;border-radius:10px;min-width:64px">
               <div style="font-size:11px;opacity:.6;text-transform:uppercase;letter-spacing:.04em">${label}</div>
               <div style="font-size:18px;font-weight:700">${val}</div>
             </div>`;
        const meter = (label, v, hue) => {
            const pct = Math.round(Math.max(0, Math.min(1, v)) * 100);
            return `<div style="background:rgba(18,22,42,.72);padding:8px 12px;border-radius:10px;min-width:120px">
               <div style="display:flex;justify-content:space-between;font-size:11px;opacity:.6;text-transform:uppercase;letter-spacing:.04em">
                 <span>${label}</span><span>${pct}</span>
               </div>
               <div style="height:6px;border-radius:3px;background:rgba(255,255,255,.12);margin-top:5px;overflow:hidden">
                 <div style="height:100%;width:${pct}%;background:${hue};transition:width .12s ease"></div>
               </div>
             </div>`;
        };
        const topRow = `
          <div style="position:absolute;top:12px;left:12px;right:12px;display:flex;gap:10px;flex-wrap:wrap">
            ${stat("Score", String(h.score))}
            ${stat("Combo", `${h.combo}x`)}
            ${meter("Agility", h.agility, "#22d3ee")}
            ${meter("Precision", h.precision, "#22d3ee")}
            ${meter("Steadiness", h.consistency, "#a78bfa")}
            ${meter("Recovery", h.recovery, "#4ade80")}
          </div>`;
        // Timing verdict banner (center-top), only when there's a fresh verdict.
        const vc = VERDICT_COLOR[h.lastVerdict] ?? VERDICT_COLOR.none;
        // Only show a fresh verdict: it must be a real event (lastEventAt > 0)
        // and recent (within 1.2s). This prevents a stale "MISS +999ms" from
        // sitting on screen from the very start of a run.
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const fresh = (h.lastEventAt ?? 0) > 0 && now - (h.lastEventAt ?? 0) < 1200;
        const showVerdict = fresh && h.lastVerdict && h.lastVerdict !== "none";
        // Scorer convention (see game/scoring.ts timingVerdict): offsetMs > 0
        // means EARLY. Rhythm-game players read "+" as late, so we flip the
        // sign for DISPLAY ONLY here (early → −, late → +). This is the single
        // place the display sign is inverted — do not flip elsewhere.
        const displayOffset = -h.lastOffsetMs;
        const offTxt = h.lastVerdict !== "miss" && h.lastOffsetMs !== 0
            ? ` <span style="opacity:.7;font-size:12px">${displayOffset > 0 ? "+" : ""}${Math.round(displayOffset)}ms</span>`
            : "";
        const gainTxt = h.lastGain > 0 ? ` +${h.lastGain}` : "";
        const verdictText = h.lastVerdict === "none"
            ? ""
            : verdictLabel(h.lastVerdict);
        const verdictBanner = showVerdict
            ? `<div style="position:absolute;top:96px;left:50%;transform:translateX(-50%);
                   background:rgba(10,12,24,.72);padding:6px 16px;border-radius:20px;
                   border:1px solid ${vc}55;box-shadow:0 0 24px ${vc}33;
                   backdrop-filter:blur(6px)">
                 <span style="color:${vc};font-weight:800;letter-spacing:.06em">
                   ${verdictText}${gainTxt}</span>${offTxt}
               </div>`
            : "";
        // Legend / key so players know which glyph maps to which sound.
        const lanes = store.activeLanes.get();
        const keyItems = lanes
            .map((id) => {
            const glyph = escapeHtml(iconFor(id, theme));
            const label = escapeHtml(labelFor(id, theme));
            return `<div style="display:flex;align-items:center;gap:6px">
                <span style="display:inline-flex;align-items:center;justify-content:center;
                    width:26px;height:26px;border-radius:50%;background:rgba(34,211,238,.25);
                    font-weight:700">${glyph}</span>
                <span style="font-size:12px;opacity:.85">${label}</span>
              </div>`;
        })
            .join("");
        const legend = `
          <div style="position:absolute;top:12px;right:12px;background:rgba(18,22,42,.72);
              padding:10px 12px;border-radius:12px;display:flex;flex-direction:column;gap:8px">
            <div style="font-size:11px;opacity:.6;text-transform:uppercase;letter-spacing:.04em">Key</div>
            ${keyItems}
          </div>`;
        this.innerHTML = topRow + verdictBanner + legend;
    }
}
customElements.define("ac-hud", AcHud);
